import { useEffect, useRef } from 'react'
import { createClient } from 'contentful'
import Error from '@/pages/error'
import useIsMobile from '@/util/useIsMobile'
import BlogSwiper from '@/components/blogSwiper'
import RelatedPosts from '@/components/RelatedPosts'
import { IImageData } from '@/util/types'
import checkIfObjIsFull from '@/util/checkIfObjIsFull'
import { urlParser } from '@/util/imgHelpers'
import { useRouter } from 'next/router'
import { useRecoilValue } from 'recoil'
import { subDataState } from '@/util/states'
import Head from 'next/head'
import ReactMarkdown from 'react-markdown'


export async function getServerSideProps(context: any) {
  const redirects: any  = {
    "sam-kolder-interview": "/blogs/interviews/samkolder",
    "fuji-editorial": "/editorials/fiji",
    "st-tropez-adi-roni": "/editorials/st-tropez-adi-roni",
    "fuji-pt-2": "/editorials/fiji-pt-2",
    "fuji-pt-3": "/editorials/fiji-pt-3",
    "positano-celosa": "/editorials/positano-celosa",
    "fiji-pt-4": "/editorials/fiji-pt-4",
    "ibiza-noemi": "/editorials/ibiza-noemi",
    "anna-copenhagen": "/editorials/anna-copenhagen",
    "infocus-anna": "/editorials/infocus-anna",
    "infocus-isabella": "/editorials/infocus-isabella",
    "sunday-kitchen": "/editorials/sunday-kitchen",
    "afternoon-in-tokyo": "/editorials/afternoon-in-tokyo",
    "rambagh-palace": "/editorials/rambagh-palace",
    "artist-studies-ro-starr": "/editorials/artist-studies-ro-starr",
    "a-garden-that-remembers-her-softly": "/editorials/a-garden-that-remembers-her-softly",
    "where-the-light-paused-for-us": "/editorials/where-the-light-paused-for-us",
    "what-was-already-there": "/editorials/what-was-already-there",
    "the-last-light-with-lucca": "/editorials/the-last-light-with-lucca",
    "the-afternoon-between-conversations": "/editorials/the-afternoon-between-conversations",
    "the-architecture-of-leisure": "/editorials/the-architecture-of-leisure",
    "the-last-room-before-evening": "/editorials/the-last-room-before-evening",
    "where-light-falls-architecture-begins-to-speak": "/editorials/where-light-falls-architecture-begins-to-speak",
    "the-weight-of-wonder": "/editorials/the-weight-of-wonder",
    "bedouin-bali": "/editorials/bedouin-bali",
    "summer-afternoon-with-elisha": "/editorials/summer-afternoon-with-elisha",
    "a-summer-at-the-villa": "/editorials/a-summer-at-the-villa",
    "four-days-at-the-edge-of-komodo": "/editorials/four-days-at-the-edge-of-komodo",
    "a-familiar-light": "/editorials/a-familiar-light",
    "the-space-between-introductions": "/editorials/the-space-between-introductions",
    "a-week-outside-of-time": "/editorials/a-week-outside-of-time",
    "what-the-hands-remember": "/editorials/what-the-hands-remember",
    "before-the-world-wakes": "/editorials/before-the-world-wakes",
    "a-body-in-motion": "/editorials/a-body-in-motion",
    "an-hour-with-luka": "/editorials/an-hour-with-luka",
    "europe-summer-on-film": "/editorials/europe-summer-on-film",
    "memory-of-warmer-places": "/editorials/memory-of-warmer-places",
  };

  if(redirects[context.params.blogUrl]) {
    return {
      redirect: {
        destination: redirects[context.params.blogUrl],
        permanent: false,
      },
    }
  }

  const spaceId = process.env.CONTENTFUL_SPACE_ID
  const accessToken = process.env.CONTENTFUL_ACCESS_KEY

  if (!spaceId || !accessToken) {
    throw '.env.local variables are not setup correctly.'
  }

  const client = createClient({
    space: spaceId,
    accessToken: accessToken,
    environment: process.env.CONTENTFUL_ENV,
  })

  const blogEntryPromise = client.getEntries({
    content_type: 'blogPost',
    limit: 1,
    'fields.blogUrl': context.params.blogUrl
  })

  const blogEntry:any = await blogEntryPromise

  if (blogEntry.items.length === 0) return { notFound: true }

  const getBlogTags = blogEntry.items[0].metadata?.tags.map((tag:any) => {
    return tag?.sys?.id;
  });

  const relatedPostsPromise = client.getEntries({
    content_type: 'blogPost',
    limit: 4,
    'metadata.tags.sys.id[in]': getBlogTags.join(","),
    'sys.id[ne]': blogEntry.items[0].sys?.id
  })

  const relatedPosts = await relatedPostsPromise
  const thumbnail = blogEntry.items[0].fields.thumbnail.fields.file.url
  const blogTitle = blogEntry.items[0].fields.blogTitle as string | undefined

  return {
    props: {
      blogEntry: blogEntry.items[0],
      relatedPosts: relatedPosts.items,
      image: thumbnail,
      title: blogTitle,
    }
  }
}


export default function Blog({ blogEntry, relatedPosts }:
  {
    blogEntry: { [key: string]: any },
    relatedPosts: { [key: string]: any },
  }) {
  const subData = useRecoilValue(subDataState)
  const isMobile = useIsMobile()
  const router = useRouter()

  if (isMobile === undefined) return null
  if (subData === 'pending') return null

  const blogType = blogEntry.fields.blogType as string | undefined
  const blogTitle = blogEntry.fields.blogTitle as string | undefined
  const datePublished = blogEntry.fields.datePublished as string | undefined
  const author = blogEntry.fields.author as string | undefined
  const subscriptionRequired = blogEntry.fields.subscriptionRequired?.fields.name
  const tags = blogEntry.fields?.tags as string | undefined
  const blogBody = blogEntry.fields.blogBody as string | undefined

  const isAllowedToViewContent =
    !subscriptionRequired
    || subData.tier === 'Beyond'
    || (
      subscriptionRequired === 'Standard'
      && subData.tier === 'Standard'
    )
    || subData?.unlocked?.includes(blogEntry.sys.id)

  if (!isAllowedToViewContent) {
    router.push('/subscribe')
    return null
  }

  const BlogHeader = () =>
    <div className="mt-14 mb-8 text-center relative">

    <div className="mb-2 text-xs font-soehne-kraftig text-light">{datePublished || ''}</div>
    <h1 className="font-soehne-kraftig tracking-wide text-xl dt:text-2xl dt:mt-2" style={{ lineHeight: isMobile ? undefined : 1.25 }}>
      {blogTitle || ''}
    </h1>
    <div className="mt-1 font-soehne-kraftig text-xs text-light">{author || ''}</div>
  </div>

  const BlogBody = () => {
    if (!blogBody) return null
    return (
      <div className="mt-6 dt:ml-8 font-soehne-leight blog-body"><ReactMarkdown>{blogBody}</ReactMarkdown></div>
    )
  }

  const Gallery = () => {

    const media = blogEntry.fields?.media as { [key: string]: any }[] | undefined
    if (!media) return null

    const unfilteredGalleryDatas = media.map((item) => {
      const galleryData = {
        imageUrl: urlParser(item.fields?.file.url, {
          width: isMobile ? 800 : 1200,
          height: isMobile ? 1000 : 1500,
          format: 'webp',
          quality: 90
        }),
        imgDimentions: {
          x: item.fields?.file?.details.image?.width,
          y: item.fields?.file?.details.image?.height
        }
      }

      const isGalleryDataFull = checkIfObjIsFull(galleryData)
      if (isGalleryDataFull) return galleryData
    })

    const galleryDatas = unfilteredGalleryDatas.filter( (galleryData) => {
      if (!galleryData) return false
      return true
    }) as IImageData[]
  
    return (
      <div className="mb:w-full dt:w-3/5 mx-auto">
        <div className="mx-auto font-soehne-kraftig text-md" style={{ width: '85%' }}>
          <BlogHeader />
          <BlogSwiper imageDatas={galleryDatas!} isMobile={isMobile} />
          <BlogBody />
        </div>

      </div>
    )
  }

  const Video = () => {

    const iframeWrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (iframeWrapperRef.current) {
        iframeWrapperRef.current.children[0].classList.add("dt:w-1/2")
        iframeWrapperRef.current.children[0].classList.add("dt:mx-auto")
        iframeWrapperRef.current.children[0].classList.add("blog-video")
      }
    }, [iframeWrapperRef.current])

    let videoEmbedCode = blogEntry.fields?.videoEmbedCode.content[0].content[0].value as string | undefined
    if (!videoEmbedCode) return <Error />

    return (
      <div className="dt:mx-auto">
        <div className="pt-5 relative mb:w-11/12 mx-auto">
          <BlogHeader />
        </div>
        <div ref={iframeWrapperRef} dangerouslySetInnerHTML={{__html: videoEmbedCode}} />
        <div className='mb:px-8'>
          <BlogBody />
        </div>
      </div>
    )
  }

  return (
    <>
      <Head>
        <title>{blogTitle || ''}</title>
        <meta
          name="description"
          content={`A blog post for Beyond The Pines Magazine entitled, "${blogTitle || ''}". Beyond The Pines Magazine: breathtaking photography and visual provocation that stimulates thoughts, ideas, & conversations.`}
        />
        <meta
          name="keywords"
          content={
            tags +
            ', Beyond The Pines, Beyond The Pines Magazine, Beyond The Pines Mag, magazine, mag, BTP, BTP magazine, BTP mag, blog, Beyond The Pines blog, BTP blog'
          }
        />
        <meta name="author" content={author} />
      </Head>
      <div className="pb-height-footer">
        {blogType === 'gallery' ? <Gallery /> : null}
        {blogType === 'video' ? <Video /> : null}
      </div>
      {relatedPosts.length > 2 ? <RelatedPosts posts={relatedPosts} /> : null}
    </>
  )
}
